// Runtime theme switching (follow-up F4, walkthrough finding: WALKTHROUGH.md
// describes a theme switch in the shell header, but only the blocking boot
// script in targets/*/index.html ever set `data-theme`).
//
// The boot script stays the single first-paint authority and is NOT touched
// here (it is admitted to the CSP by hash — src/test/csp.test.ts). This module
// mirrors its rule exactly (theme.test.ts evaluates THEME_BOOT_SCRIPT itself
// and compares) and writes the same localStorage key, so a choice made here is
// what the boot script applies on the next load:
//   'dark'            → dark
//   'light'           → light
//   anything else     → follow the OS (prefers-color-scheme)
// "System" is stored as no key at all.

export const THEME_STORAGE_KEY = 'ng-theme';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const DARK_QUERY = '(prefers-color-scheme: dark)';

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // storage blocked (privacy mode / sandboxed frame)
  }
}

export function readThemePreference(): ThemePreference {
  try {
    const v = storage()?.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function systemPrefersDark(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

/** Same rule as the boot script. */
export function resolveTheme(pref: ThemePreference, prefersDark: boolean = systemPrefersDark()): ResolvedTheme {
  return pref === 'dark' || (pref !== 'light' && prefersDark) ? 'dark' : 'light';
}

export function applyResolvedTheme(theme: ResolvedTheme): void {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', theme);
}

// The choice made in this page view — authoritative even when storage is
// blocked and the write below silently failed.
let pagePreference: ThemePreference | null = null;

export function currentThemePreference(): ThemePreference {
  return pagePreference ?? readThemePreference();
}

/** Persist the choice (System removes the key) and apply it now. */
export function setThemePreference(pref: ThemePreference): ResolvedTheme {
  pagePreference = pref;
  try {
    const s = storage();
    if (pref === 'system') s?.removeItem(THEME_STORAGE_KEY);
    else s?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
  const resolved = resolveTheme(pref);
  applyResolvedTheme(resolved);
  return resolved;
}

/**
 * Keeps `data-theme` current while the page is open: follows the OS when the
 * preference is System, and picks up a choice made in another tab (the
 * `storage` event). Returns an unsubscribe function.
 */
export function watchTheme(onPrefChange: (pref: ThemePreference) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  if (typeof window.matchMedia === 'function') {
    const mql = window.matchMedia(DARK_QUERY);
    const onMedia = () => {
      if (currentThemePreference() === 'system') applyResolvedTheme(resolveTheme('system', mql.matches));
    };
    mql.addEventListener?.('change', onMedia);
    cleanups.push(() => mql.removeEventListener?.('change', onMedia));
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== THEME_STORAGE_KEY) return;
    const pref = readThemePreference();
    pagePreference = pref;
    applyResolvedTheme(resolveTheme(pref));
    onPrefChange(pref);
  };
  window.addEventListener('storage', onStorage);
  cleanups.push(() => window.removeEventListener('storage', onStorage));

  return () => cleanups.forEach((c) => c());
}

/** Test seam: forget this page view's choice. */
export function resetThemePreferenceForTests(): void {
  pagePreference = null;
}
